import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { formatRent, formatDate, formatTimeAgo, userTypeLabel } from '../../utils/format';

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, to }) {
  const inner = (
    <div className={`card p-3.5 sm:p-5 group transition-all duration-200 ${to ? 'cursor-pointer hover:border-[#C8A800]/40' : ''}`}>
      <div className="text-[#CCCCCC] mb-2 sm:mb-3">{icon}</div>
      <p className={`text-2xl sm:text-[32px] font-bold leading-none tracking-tight mb-1 ${value === 0 || value === '0' ? 'text-[#DDDDDD]' : 'text-[#1B1B1B]'}`}>
        {value === 0 || value === '0' ? '—' : value}
      </p>
      <p className="text-xs sm:text-sm font-medium text-[#555555] nav-link">{label}</p>
      {sub && <p className="text-xs text-[#AAAAAA] mt-0.5 truncate nav-link hidden sm:block">{sub}</p>}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

// ─── Section header with divider ────────────────────────────────────────────────
function SectionHeader({ title, to, linkLabel = 'See all' }) {
  return (
    <div className="mb-4 sm:mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base sm:text-xl font-extrabold text-[#1B1B1B] tracking-tight">{title}</h2>
        {to && (
          <Link to={to} className="text-sm text-[#C8A800] hover:text-[#B89700] font-medium flex items-center gap-1 nav-link transition-colors">
            {linkLabel}
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        )}
      </div>
      <div className="border-b border-[#EBEBEB]" />
    </div>
  );
}

// ─── Quick actions — pill buttons ─────────────────────────────────────────────
function QuickActions({ isLandlord }) {
  const links = isLandlord
    ? [
        { to: '/listings', label: 'Post a Listing' },
        { to: '/listings', label: 'My Listings' },
        { to: '/messages', label: 'Messages' },
        { to: '/payments', label: 'Stripe Connect' },
      ]
    : [
        { to: '/roommates', label: 'Find a Roommate' },
        { to: '/listings', label: 'Browse Apartments' },
        { to: '/subleases', label: 'Subleases' },
        { to: '/messages', label: 'Messages' },
        { to: '/payments', label: 'Pay Rent' },
      ];

  return (
    <div className="mb-6 sm:mb-10 flex flex-wrap gap-2">
      {links.map(link => (
        <Link
          key={link.to + link.label}
          to={link.to}
          className="nav-link text-sm font-medium px-4 py-2 rounded-full transition-all duration-150 whitespace-nowrap"
          style={{ background: '#F9F6E8', color: '#555555', border: '1px solid rgba(200,168,0,0.35)' }}
          onMouseEnter={e => { e.currentTarget.style.background = '#C8A800'; e.currentTarget.style.color = '#1B1B1B'; e.currentTarget.style.border = '1px solid #C8A800'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#F9F6E8'; e.currentTarget.style.color = '#555555'; e.currentTarget.style.border = '1px solid rgba(200,168,0,0.35)'; }}
        >
          {link.label}
        </Link>
      ))}
    </div>
  );
}

// ─── Roommate match row ────────────────────────────────────────────────────────
function MatchRow({ match, navigate }) {
  const score = match.compatibility_score;
  const color = score >= 80 ? '#C8A800' : score >= 60 ? '#16a34a' : '#d97706';
  return (
    <div className="flex items-center gap-4 py-4 border-b border-[#F5F5F5] last:border-0">
      {match.avatar_url ? (
        <img src={match.avatar_url} alt={match.match_name} className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-[#E8E8E8]" />
      ) : (
        <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm" style={{ backgroundColor: 'rgba(200,168,0,0.10)', color: '#C8A800', fontFamily: 'Inter, sans-serif' }}>
          {match.match_name?.[0]?.toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-[#1B1B1B] text-sm">{match.match_name}</p>
        <p className="text-xs text-[#999999] mt-0.5 nav-link">{match.major || 'No major listed'}</p>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        <span className="text-sm font-semibold nav-link" style={{ color }}>{score}%</span>
        {match.status === 'accepted' && match.conversation_id && (
          <button
            onClick={() => navigate(`/messages/${match.conversation_id}`)}
            className="text-xs px-3 py-1.5 rounded-lg border border-[#E8E8E8] text-[#555555] hover:border-[#C8A800] hover:text-[#C8A800] transition-colors nav-link"
          >
            Chat
          </button>
        )}
        {match.status === 'pending' && (
          <span className="text-xs text-[#999999] nav-link">Pending</span>
        )}
      </div>
    </div>
  );
}

// ─── Listing card (mini) ───────────────────────────────────────────────────────
function MiniListingCard({ listing, type }) {
  const photo = listing.photos?.[0];
  return (
    <Link to={`/${type === 'sublease' ? 'subleases' : 'listings'}`} className="flex items-center gap-4 py-4 border-b border-[#F5F5F5] last:border-0 hover:bg-[#FAFAFA] rounded-lg px-2 -mx-2 transition-colors group">
      <div className="w-14 h-14 rounded-lg bg-[#F0F0F0] overflow-hidden flex-shrink-0 border border-[#E8E8E8]">
        {photo ? (
          <img src={photo} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#CCCCCC]">
            {type === 'sublease' ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            )}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-[#1B1B1B] text-sm truncate">{listing.property_name || listing.address}</p>
        <p className="text-xs text-[#999999] mt-0.5 truncate nav-link">{listing.address || listing.city}</p>
      </div>
      <div className="flex-shrink-0 text-right">
        <p className="text-sm font-bold text-[#1B1B1B] nav-link">{formatRent(listing.monthly_rent)}</p>
        <p className="text-xs text-[#AAAAAA] nav-link">{listing.bedrooms} BR</p>
      </div>
    </Link>
  );
}

// ─── Conversation row ──────────────────────────────────────────────────────────
function ConvRow({ conv }) {
  return (
    <Link to={`/messages/${conv.id}`} className="flex items-center gap-4 py-4 border-b border-[#F5F5F5] last:border-0 hover:bg-[#FAFAFA] rounded-lg px-2 -mx-2 transition-colors">
      {conv.other_avatar ? (
        <img src={conv.other_avatar} alt={conv.other_name} className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-[#E8E8E8]" />
      ) : (
        <div className="w-10 h-10 rounded-full bg-[#F0F0F0] flex items-center justify-center text-[#AAAAAA] font-bold flex-shrink-0 text-sm nav-link">
          {conv.other_name?.[0]?.toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-[#1B1B1B] text-sm">{conv.other_name}</p>
        <p className="text-xs text-[#999999] truncate mt-0.5 nav-link">{conv.last_message || 'No messages yet'}</p>
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        {conv.last_message_at && <span className="text-xs text-[#BBBBBB] nav-link">{formatTimeAgo(conv.last_message_at)}</span>}
        {conv.unread_count > 0 && (
          <span className="w-5 h-5 bg-[#C8A800] text-white text-xs font-bold rounded-full flex items-center justify-center nav-link">
            {conv.unread_count}
          </span>
        )}
      </div>
    </Link>
  );
}

// ─── Payment row ───────────────────────────────────────────────────────────────
function PaymentRow({ payment, userId }) {
  const isStudent = payment.student_id === userId;
  const amount = isStudent ? payment.total_charged : payment.landlord_payout;
  const sign = isStudent ? '−' : '+';
  const color = isStudent ? '#CC3333' : '#16a34a';
  return (
    <div className="flex items-center gap-4 py-4 border-b border-[#F5F5F5] last:border-0">
      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 border ${isStudent ? 'bg-red-50 border-red-100' : 'bg-green-50 border-green-100'}`}>
        {isStudent ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4 text-red-500"><path strokeLinecap="round" strokeLinejoin="round" d="M17 13l-5 5-5-5M12 6v12"/></svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4 text-green-600"><path strokeLinecap="round" strokeLinejoin="round" d="M7 11l5-5 5 5M12 6v12"/></svg>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-[#1B1B1B] text-sm">{payment.property_name || 'Rent Payment'}</p>
        <p className="text-xs text-[#999999] nav-link">{isStudent ? `To ${payment.landlord_name}` : `From ${payment.student_name}`}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-sm font-bold nav-link" style={{ color }}>{sign}${(amount / 100).toFixed(2)}</p>
        <p className="text-xs text-[#AAAAAA] nav-link">{formatDate(payment.created_at)}</p>
      </div>
    </div>
  );
}

// ─── Main Dashboard ────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user } = useAuth();
  const { notifications } = useSocket();
  const navigate = useNavigate();
  const isLandlord = user?.user_type === 'landlord';

  const [data, setData] = useState({
    matches: [],
    conversations: [],
    listings: [],
    subleases: [],
    payments: [],
    unreadCount: 0,
    hasQuiz: false,
    stripeConnected: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (notifications.length) {
      api.get('/notifications/unread-count').then(r => {
        setData(prev => ({ ...prev, unreadCount: r.data.count }));
      }).catch(() => {});
    }
  }, [notifications]);

  async function loadAll() {
    setLoading(true);
    try {
      const calls = [
        api.get('/messages/conversations').catch(() => ({ data: [] })),
        api.get('/notifications/unread-count').catch(() => ({ data: { count: 0 } })),
        api.get('/payments/history').catch(() => ({ data: [] })),
      ];
      if (!isLandlord) {
        calls.push(api.get('/roommates/matches').catch(() => ({ data: [] })));
        calls.push(api.get('/roommates/quiz').catch(() => ({ data: null })));
      } else {
        calls.push(api.get('/listings/my/listings').catch(() => ({ data: [] })));
        calls.push(api.get('/payments/connect/status').catch(() => ({ data: { onboarding_complete: false } })));
      }
      calls.push(api.get('/listings?limit=4').catch(() => ({ data: { listings: [] } })));
      calls.push(api.get('/subleases?limit=4').catch(() => ({ data: { subleases: [] } })));

      const results = await Promise.all(calls);
      const [convsRes, unreadRes, paymentsRes, fourthRes, fifthRes, listingsRes, subleasesRes] = results;

      setData({
        conversations: convsRes.data.slice(0, 5),
        unreadCount: unreadRes.data.count,
        payments: paymentsRes.data.slice(0, 4),
        matches: !isLandlord ? (fourthRes.data || []).slice(0, 5) : [],
        myListings: isLandlord ? (fourthRes.data || []).slice(0, 3) : [],
        hasQuiz: !isLandlord ? !!fifthRes.data : false,
        stripeConnected: isLandlord ? fifthRes.data?.onboarding_complete : false,
        listings: listingsRes.data.listings?.slice(0, 3) || [],
        subleases: subleasesRes.data.subleases?.slice(0, 3) || [],
      });
    } catch (err) {
      console.error('Dashboard load error:', err);
    }
    setLoading(false);
  }

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.name?.split(' ')[0];

  const totalMessages = data.conversations.reduce((sum, c) => sum + (parseInt(c.unread_count) || 0), 0);
  const acceptedMatches = data.matches?.filter(m => m.status === 'accepted').length || 0;
  const pendingMatches = data.matches?.filter(m => m.status === 'pending').length || 0;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3">
        <div className="w-8 h-8 border-2 border-[#C8A800] border-t-transparent rounded-full animate-spin" />
        <p className="text-[#AAAAAA] text-sm nav-link">Loading your dashboard…</p>
      </div>
    );
  }

  return (
    <div className="page-container max-w-7xl">

      {/* ── Greeting banner ── */}
      <div className="relative rounded-xl sm:rounded-2xl overflow-hidden mb-6 sm:mb-10" style={{ background: '#1B1B1B' }}>
        {/* Skyline silhouette — right side */}
        <svg viewBox="0 0 800 180" preserveAspectRatio="xMaxYMax meet" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"
          style={{ position: 'absolute', right: 0, bottom: 0, height: '100%', width: '70%', pointerEvents: 'none', opacity: 0.85 }}>
          <g fill="#252525">
            <rect x="0"   y="120" width="40" height="60" />
            <rect x="42"  y="100" width="30" height="80" />
            <rect x="74"  y="80"  width="50" height="100" />
            <rect x="82"  y="65"  width="12" height="15" />
            <rect x="126" y="110" width="35" height="70" />
            <rect x="163" y="70"  width="60" height="110" />
            <rect x="178" y="55"  width="14" height="15" />
            <rect x="170" y="82"  width="7" height="5" fill="#2A2A2A" />
            <rect x="182" y="82"  width="7" height="5" fill="#2A2A2A" />
            <rect x="170" y="93"  width="7" height="5" fill="#2A2A2A" />
            <rect x="182" y="93"  width="7" height="5" fill="#2A2A2A" />
            <rect x="225" y="95"  width="45" height="85" />
            <rect x="272" y="115" width="32" height="65" />
            <rect x="306" y="60"  width="65" height="120" />
            <rect x="323" y="44"  width="16" height="16" />
            <rect x="312" y="72"  width="8" height="5" fill="#2A2A2A" />
            <rect x="325" y="72"  width="8" height="5" fill="#2A2A2A" />
            <rect x="338" y="72"  width="8" height="5" fill="#2A2A2A" />
            <rect x="312" y="84"  width="8" height="5" fill="#2A2A2A" />
            <rect x="325" y="84"  width="8" height="5" fill="#2A2A2A" />
            <rect x="338" y="84"  width="8" height="5" fill="#2A2A2A" />
            <rect x="312" y="96"  width="8" height="5" fill="#2A2A2A" />
            <rect x="325" y="96"  width="8" height="5" fill="#2A2A2A" />
            <rect x="373" y="90"  width="48" height="90" />
            <rect x="383" y="75"  width="12" height="15" />
            <rect x="423" y="105" width="38" height="75" />
            <rect x="463" y="75"  width="55" height="105" />
            <rect x="476" y="60"  width="13" height="15" />
            <rect x="520" y="118" width="33" height="62" />
            <rect x="555" y="88"  width="50" height="92" />
            <rect x="607" y="100" width="40" height="80" />
            <rect x="649" y="72"  width="58" height="108" />
            <rect x="662" y="57"  width="14" height="15" />
            <rect x="709" y="112" width="36" height="68" />
            <rect x="747" y="85"  width="53" height="95" />
          </g>
          <rect x="0" y="178" width="800" height="2" fill="#252525" />
        </svg>

        {/* Content */}
        <div className="relative z-10 flex flex-row items-center justify-between gap-3 px-5 py-5 sm:px-8 sm:py-10">
          <div>
            <p className="text-[10px] sm:text-xs uppercase tracking-widest mb-1.5 sm:mb-3 nav-link" style={{ color: 'rgba(255,255,255,0.45)' }}>
              {userTypeLabel(user?.user_type)} · {user?.school}
            </p>
            <h1 className="text-xl sm:text-4xl font-bold tracking-tight inline-block" style={{ color: '#FFFFFF', fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.01em' }}>
              {greeting}, {firstName}.
            </h1>
            <div style={{ height: 3, width: '100%', background: '#C8A800', borderRadius: 2, marginTop: 6, opacity: 0.85 }} />
          </div>
          <Link to="/profile" className="flex items-center gap-2.5 hover:opacity-75 transition-opacity flex-shrink-0">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" className="w-9 h-9 sm:w-11 sm:h-11 rounded-full object-cover flex-shrink-0" style={{ border: '2px solid rgba(255,255,255,0.15)' }} />
            ) : (
              <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0" style={{ backgroundColor: 'rgba(200,168,0,0.20)', color: '#C8A800', border: '2px solid rgba(200,168,0,0.35)' }}>
                {user?.name?.[0]?.toUpperCase()}
              </div>
            )}
            <div className="hidden sm:block">
              <p className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>{user?.name}</p>
              <p className="text-xs nav-link" style={{ color: 'rgba(255,255,255,0.4)' }}>View profile →</p>
            </div>
          </Link>
        </div>
      </div>

      {/* ── Alert banners ── */}
      {(!data.stripeConnected || (!isLandlord && !data.hasQuiz)) && (
        <div className="flex flex-col sm:flex-row gap-2 mb-6">
          {!data.stripeConnected && (
            <div className="flex items-center gap-2.5 flex-1 rounded-lg border border-[#E8C840]/40 bg-[#FFFDF0] px-3.5 py-2.5">
              <svg className="w-3.5 h-3.5 text-[#C8A800] flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
              <p className="text-xs font-medium text-[#8A7000] flex-1 nav-link">Link your bank to receive payments</p>
              <Link to="/payments?tab=connect" className="text-xs font-semibold text-[#C8A800] hover:text-[#B89700] nav-link transition-colors flex-shrink-0">Connect →</Link>
            </div>
          )}
          {!isLandlord && !data.hasQuiz && (
            <div className="flex items-center gap-2.5 flex-1 rounded-lg border border-[#E8E8E8] bg-white px-3.5 py-2.5">
              <svg className="w-3.5 h-3.5 text-[#AAAAAA] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
              <p className="text-xs font-medium text-[#555555] flex-1 nav-link">Complete your roommate compatibility quiz</p>
              <Link to="/roommates" className="text-xs font-semibold text-[#C8A800] hover:text-[#B89700] nav-link transition-colors flex-shrink-0">Take Quiz →</Link>
            </div>
          )}
        </div>
      )}

      {/* ── Quick Actions ── */}
      <QuickActions isLandlord={isLandlord} />

      {/* ── Stats row ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-10">
        {!isLandlord ? (
          <>
            <StatCard
              icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
              label="Accepted Matches" value={acceptedMatches}
              sub={pendingMatches > 0 ? `${pendingMatches} pending` : 'None pending'}
              to="/roommates" color="gold"
            />
            <StatCard
              icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>}
              label="Unread Messages" value={totalMessages}
              sub={`${data.conversations.length} conversations`}
              to="/messages" color="blue"
            />
            <StatCard
              icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>}
              label="Active Listings" value={data.listings.length}
              sub="Near campus"
              to="/listings" color="purple"
            />
            <StatCard
              icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>}
              label="Payments Due" value={data.payments.length}
              sub="Tap to pay rent"
              to="/payments" color="green"
            />
          </>
        ) : (
          <>
            <StatCard
              icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>}
              label="My Listings" value={data.myListings?.length || 0}
              sub="Active properties"
              to="/listings" color="gold"
            />
            <StatCard
              icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>}
              label="Unread Messages" value={totalMessages}
              sub={`${data.conversations.length} conversations`}
              to="/messages" color="blue"
            />
            <StatCard
              icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>}
              label="Payments Received" value={data.payments.length}
              sub="Total"
              to="/payments" color="green"
            />
            <StatCard
              icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/></svg>}
              label="Stripe" value={data.stripeConnected ? 'Active' : 'Setup needed'}
              sub={data.stripeConnected ? 'Payouts enabled' : 'Action required'}
              to="/payments?tab=connect" color={data.stripeConnected ? 'green' : 'gold'}
            />
          </>
        )}
      </div>

      {/* ── Main content grid ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

        {/* Left col — 2 wide */}
        <div className="lg:col-span-2 space-y-8">

          {/* Roommate matches */}
          {!isLandlord && (
            <div className="card p-4 sm:p-6">
              <SectionHeader title="Roommate Matches" to="/roommates" />
              {data.matches.length === 0 ? (
                <div className="text-center py-10">
                  <svg className="w-8 h-8 text-[#DDDDDD] mb-3 mx-auto" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35"/></svg>
                  <p className="text-[#999999] text-sm nav-link">
                    {data.hasQuiz ? 'No new matches yet — check the discover tab.' : 'Complete the quiz to see your matches.'}
                  </p>
                  <Link to="/roommates" className="inline-block mt-3 text-sm text-[#C8A800] hover:text-[#B89700] nav-link transition-colors">
                    {data.hasQuiz ? 'Browse matches →' : 'Take quiz →'}
                  </Link>
                </div>
              ) : (
                <div>
                  {data.matches.map(m => <MatchRow key={m.id} match={m} navigate={navigate} />)}
                </div>
              )}
            </div>
          )}

          {/* My Listings (landlords) */}
          {isLandlord && (
            <div className="card p-4 sm:p-6">
              <SectionHeader title="My Listings" to="/listings" linkLabel="Manage" />
              {!data.myListings?.length ? (
                <div className="text-center py-10">
                  <svg className="w-8 h-8 text-[#DDDDDD] mb-3 mx-auto" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                  <p className="text-[#999999] text-sm nav-link">No listings yet</p>
                  <Link to="/listings" className="inline-block mt-3 text-sm text-[#C8A800] hover:text-[#B89700] nav-link transition-colors">Post your first listing →</Link>
                </div>
              ) : (
                <div>{data.myListings.map(l => <MiniListingCard key={l.id} listing={l} type="apartment" />)}</div>
              )}
            </div>
          )}

          {/* Recent listings */}
          <div className="card p-4 sm:p-6">
            <SectionHeader title="New Apartment Listings" to="/listings" />
            {data.listings.length === 0 ? (
              <p className="text-[#AAAAAA] text-sm text-center py-8 nav-link">No listings available right now</p>
            ) : (
              <div>{data.listings.map(l => <MiniListingCard key={l.id} listing={l} type="apartment" />)}</div>
            )}
          </div>

          {/* Recent subleases */}
          <div className="card p-4 sm:p-6">
            <SectionHeader title="Recent Subleases" to="/subleases" />
            {data.subleases.length === 0 ? (
              <p className="text-[#AAAAAA] text-sm text-center py-8 nav-link">No subleases listed right now</p>
            ) : (
              <div>{data.subleases.map(s => <MiniListingCard key={s.id} listing={s} type="sublease" />)}</div>
            )}
          </div>
        </div>

        {/* Right col */}
        <div className="space-y-8">

          {/* Messages */}
          <div className="card p-4 sm:p-6">
            <SectionHeader title="Recent Messages" to="/messages" />
            {data.conversations.length === 0 ? (
              <div className="text-center py-10">
                <svg className="w-8 h-8 text-[#DDDDDD] mb-3 mx-auto" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                <p className="text-[#AAAAAA] text-sm nav-link">No conversations yet</p>
              </div>
            ) : (
              <div>{data.conversations.map(c => <ConvRow key={c.id} conv={c} />)}</div>
            )}
          </div>

          {/* Payment history */}
          <div className="card p-4 sm:p-6">
            <SectionHeader title={isLandlord ? 'Recent Payouts' : 'Recent Payments'} to="/payments" />
            {data.payments.length === 0 ? (
              <div className="text-center py-10">
                <svg className="w-8 h-8 text-[#DDDDDD] mb-3 mx-auto" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                <p className="text-[#AAAAAA] text-sm nav-link">No payment history yet</p>
                {!isLandlord && (
                  <Link to="/payments" className="inline-block mt-3 text-sm text-[#C8A800] hover:text-[#B89700] nav-link transition-colors">Pay rent →</Link>
                )}
              </div>
            ) : (
              <div>{data.payments.map(p => <PaymentRow key={p.id} payment={p} userId={user?.id} />)}</div>
            )}
          </div>

          {/* Iowa City updates */}
          <div className="card p-4 sm:p-6">
            <SectionHeader title="Iowa City" />
            <div>
              {[
                'University of Iowa fall semester housing opens soon.',
                'Kinnick Stadium game days — plan your move-in around the schedule.',
                'New roommate profiles added this week.',
                'Winter sublease season is active in Iowa City.',
              ].map((item, i) => (
                <div key={i} className="py-3.5 border-b border-[#F5F5F5] last:border-0 flex items-start gap-2.5">
                  <div className="w-1 h-1 rounded-full bg-[#C8A800] mt-2 flex-shrink-0" />
                  <p className="text-sm text-[#555555] leading-relaxed nav-link">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
